import type { View } from "@-/com.npmjs.react-native";
import {
  type AnimatedRef,
  measure,
  useFrameCallback,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useContext, useLayoutEffect } from "react";
import {
  ToolbarSheetShadowPublisherContext,
  ToolbarSheetShadowVisibleContext,
  type ToolbarSheetShadowFrame,
} from "#toolbar-sheet-shadow-context";
/**
 * Only the shadow crosses the native screen boundary. The real sheet, scroll view and handle
 * stay in their original gesture/layout tree; neither toolbar controls nor bottom tabs move.
 */
export function useToolbarSheetShadow(ref: AnimatedRef<View>): boolean {
  const publish = useContext(ToolbarSheetShadowPublisherContext);
  const visible = useContext(ToolbarSheetShadowVisibleContext);
  const frame = useSharedValue<ToolbarSheetShadowFrame | null>(null);
  const active = publish !== null && visible;
  const callback = useFrameCallback(() => {
    const bounds = measure(ref);
    const previous = frame.get();
    if (bounds === null || bounds.width <= 0 || bounds.height <= 0) {
      if (previous !== null) {frame.set(null);}
    } else if (
      previous === null ||
      previous.height !== bounds.height ||
      previous.width !== bounds.width ||
      previous.x !== bounds.pageX ||
      previous.y !== bounds.pageY
    ) {
      frame.set({ height: bounds.height, width: bounds.width, x: bounds.pageX, y: bounds.pageY });
    }
  }, false);
  useLayoutEffect(() => {
    if (!active) {
      return;
    }
    const unpublish = publish(frame);
    callback.setActive(true);
    return () => {
      callback.setActive(false);
      frame.set(null);
      unpublish();
    };
  }, [active, callback, frame, publish]);
  return publish !== null;
}
